/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { EmailRegex, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Note } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { ErrorComponent } from '@msdyn365-commerce-modules/checkout';

import { IExtCheckoutGuestProfileData } from './ext-checkout-guest-profile.data';
import { IExtCheckoutGuestProfileProps } from './ext-checkout-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getAddCustomerRefInfo, { IAddCustomerRefInfo } from './components/get-add-customer-reference-info';
import getAddPurchaseOrderInfo, { IAddPurchaseOrderInfo } from './components/get-add-customer-requisition-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';
import getNoteInfo, { IShowNoteInfo } from './components/get-show-note-info';
import getCustomerRefInfo, { IShowCustomerRefInfo } from './components/get-show-customer-reference-info';
import getPurchaseOrderInfo, { IShowPurchaseOrderInfo } from './components/get-show-customer-requisition-info';
import getAddNoteInfo, { IAddNoteInfo } from './components/get-add-note-info';

interface ICheckoutGuestProfileState {
    email: string;
    customerRequisition?: string;
    customerReference?: string;
    note?: Note;
    hasCheckoutError?: boolean;
    errorMessage?: string;
}

class BsEmailRegex extends EmailRegex {
    static get defaultRegex(): RegExp {
        return EmailRegex.html5EmailRegex;
    }

    //NOTE: BS CUSTOM - requires >=2 characters after .
    static get bsEmailRegex(): RegExp {
        return new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$');
    }
}

export interface ICheckoutGuestProfileModuleProps extends IExtCheckoutGuestProfileProps<IExtCheckoutGuestProfileData>, IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps
    extends IExtCheckoutGuestProfileProps<IExtCheckoutGuestProfileData>,
        ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    showCustomerRefInfo?: IShowCustomerRefInfo;
    showPurchaseOrderInfo?: IShowPurchaseOrderInfo;
    showNoteInfo?: IShowNoteInfo;
    addPurchaseOrderInfo?: IAddPurchaseOrderInfo;
    addCustomerRefInfo?: IAddCustomerRefInfo;
    addNoteInfo?: IAddNoteInfo;
    addContactInfo?: IAddContactInfo;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;

    onChange?(email: string, customerRequisition?: string, customerReference?: string, note?: Note): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<IExtCheckoutGuestProfileProps<IExtCheckoutGuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            ''
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private readonly customerReferenceRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly customerRequisitionRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly noteRef: React.RefObject<HTMLInputElement> = React.createRef();

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get hasEmailError(): boolean {
        return this.props.moduleState.hasError && !this.state.hasCheckoutError;
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                const emailAddressFromExpressCheckout = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';
                const isEmailFromExpressCheckoutDifferent =
                    !StringExtensions.isNullOrEmpty(emailAddressFromExpressCheckout) &&
                    emailAddressFromExpressCheckout !== this.state.email;

                if (
                    isExpressCheckoutApplied &&
                    (!this.props.data.checkout.result?.isExpressAddressApplied || isEmailFromExpressCheckoutDifferent)
                ) {
                    await this.submitEmail(emailAddressFromExpressCheckout);
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutGuestProfile && checkoutError.errorMessage) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutGuestProfile) {
                        focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasExternalSubmitGroup }
        } = this.props;
        const { hasCheckoutError, errorMessage } = this.state;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';
        const customerRef = this.props.data.checkout.result?.checkoutCart?.cart?.Comment ?? '';
        const customerRequisition = this.props.data.checkout.result?.checkoutCart?.cart?.CustomerRequisition ?? '';
        const note = this.props.data.checkout.result?.checkoutCart?.cart?.Notes ?? [];

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            checkoutErrorRef: this.checkoutErrorRef,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            showContactInfo: isReady
                ? getShowContactInfo({
                      canEdit: !hasExternalSubmitGroup,
                      email,
                      resources,
                      onEdit: this.onEdit
                  })
                : undefined,
            showCustomerRefInfo:
                isReady && this.props.config.enableCustomerReference
                    ? getCustomerRefInfo({
                          canEdit: !hasExternalSubmitGroup,
                          customerRef,
                          resources,
                          onEdit: this.onEdit
                      })
                    : undefined,
            showPurchaseOrderInfo:
                isReady && this.props.config.enableCustomerRequisition
                    ? getPurchaseOrderInfo({
                          canEdit: !hasExternalSubmitGroup,
                          purchaseOrder: customerRequisition,
                          resources,
                          onEdit: this.onEdit
                      })
                    : undefined,
            showNoteInfo:
                isReady && this.props.config.enableCartNote
                    ? getNoteInfo({
                          noteDescription: note !== undefined ? note[0]?.Description : ''
                      })
                    : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      email: this.state.email,
                      hasError: this.hasEmailError,
                      resources,
                      inputRef: this.inputRef,
                      onChange: this.onChange,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined,
            addPurchaseOrderInfo:
                !isReady && this.props.config.enableCustomerRequisition
                    ? getAddPurchaseOrderInfo({
                          purchaseOrderNumber: this.state.customerRequisition,
                          resources,
                          inputRef: this.customerRequisitionRef,
                          onChange: this.onChangeCustomerRequisition,
                          canSubmit: !hasExternalSubmitGroup,
                          onSubmit: this.onSubmit,
                          canCancel: !hasExternalSubmitGroup && !!email,
                          onCancel: this.onCancel,
                          hasError: this.hasEmailError
                      })
                    : undefined,
            addCustomerRefInfo:
                !isReady && this.props.config.enableCustomerReference
                    ? getAddCustomerRefInfo({
                          customerRef: this.state.customerReference,
                          resources,
                          inputRef: this.customerReferenceRef,
                          onChange: this.onChangeCustomerRef,
                          canSubmit: !hasExternalSubmitGroup,
                          onSubmit: this.onSubmit,
                          canCancel: !hasExternalSubmitGroup && !!email,
                          onCancel: this.onCancel,
                          hasError: this.hasEmailError
                      })
                    : undefined,
            addNoteInfo:
                !isReady && this.props.config.enableCartNote
                    ? getAddNoteInfo({
                          note: this.state.note,
                          resources,
                          inputRef: this.noteRef,
                          onChange: this.onChangeNote,
                          canSubmit: !hasExternalSubmitGroup,
                          onSubmit: this.onSubmit,
                          canCancel: !hasExternalSubmitGroup && !!email,
                          onCancel: this.onCancel,
                          hasError: this.hasEmailError
                      })
                    : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        const customerAccountInformation = this.props.data.accountInformation?.result;
        const checkoutCart = checkoutState?.checkoutCart.cart;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;
        let updateEmail: string = '';

        // if obo enabled and profile selected, we need to send the order status to obo user not store manager.
        if (Msdyn365.isOboRequest(this.props.context.request)) {
            updateEmail =
                customerAccountInformation && customerAccountInformation.Email !== undefined
                    ? customerAccountInformation.Email
                    : this.setErrorMsgOboUserEmailEmpty();
        } else {
            updateEmail = this.state.email;
        }

        const emailOnCart = checkoutCart?.ReceiptEmail;
        const notes = checkoutCart?.Notes !== undefined && checkoutState?.checkoutCart.cart.Notes;
        const customerRequisition = checkoutCart?.CustomerRequisition;
        const customerReference = checkoutCart?.Comment;

        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied && !StringExtensions.isNullOrEmpty(checkoutState.emailAddressFromExpressCheckout)
                  ? checkoutState.emailAddressFromExpressCheckout
                  : undefined) ??
              updateEmail;

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid
        });

        if (email) {
            notes !== undefined
                ? await this.updateGuestProfile(email, customerReference, customerRequisition, notes[0])
                : await this.updateGuestProfile(email, customerReference, customerRequisition);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }
    };

    private readonly onChangeCustomerRef = (customerRef: string): void => {
        this.setState({ customerReference: customerRef });
    };

    private readonly onChangeCustomerRequisition = (customerRequisition: string): void => {
        this.setState({ customerRequisition: customerRequisition });
    };

    private readonly onChangeNote = (note: string): void => {
        if (note === '') {
            this.setState({ note: undefined });
        } else {
            const defaultNote = this.props.config.defaultNoteTitle;
            this.setState({ note: { Description: note, Title: defaultNote, RestrictionValue: 1 } });
        }
    };

    private readonly setErrorMsgOboUserEmailEmpty = (): string => {
        // Clear prior error
        this.props.moduleState.setHasError(false);
        this.setState({
            hasCheckoutError: false
        });
        return '';
    };

    //BS CUSTOM (USE)
    private readonly isEmailValid = (email: string): boolean => {
        return BsEmailRegex.bsEmailRegex.test(email) && EmailRegex.html5EmailRegex.test(email);
    };

    private readonly updateGuestProfile = async (
        newGuestCheckoutEmail: string,
        customerReference?: string,
        customerRequisition?: string,
        note?: Note
    ): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({
                newGuestCheckoutEmail,
                customerReference,
                customerRequisition,
                note
            });
            this.setState({ email: newGuestCheckoutEmail });
            this.setState({ note: note });
            this.setState({ customerReference: customerReference });
            this.setState({ customerRequisition: customerRequisition });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');

        const { email } = this.state;
        const { customerReference, customerRequisition, note } = this.state;
        await this.submitEmail(email, customerReference, customerRequisition, note);
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (
        email: string,
        customerReference?: string,
        customerRequisition?: string,
        note?: Note
    ): Promise<void> => {
        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else {
            this.props.moduleState.onReady();
            await this.updateGuestProfile(email, customerReference, customerRequisition, note);
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const checkoutState = this.props.data.checkout.result;
        const checkoutCart = checkoutState?.checkoutCart.cart;
        const email = checkoutState.guestCheckoutEmail;
        const notes = checkoutCart?.Notes !== undefined && checkoutState?.checkoutCart.cart.Notes;
        const customerRequisition = checkoutCart?.CustomerRequisition;
        const customerReference = checkoutCart?.Comment;
        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email
            });
            if (notes !== undefined) {
                this.setState({ note: notes[0] });
            }
            this.setState({ customerReference: customerReference });
            this.setState({ customerRequisition: customerRequisition });
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
            this.props.moduleState.onReady();
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            hasCheckoutError: true,
            errorMessage
        });
    };
}

export default CheckoutGuestProfile;
